import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/** Which table is being de-duplicated. The same word the server uses. */
export type DedupeTable = 'leads' | 'customers' | 'rolloff-hires';

/** How a column's values are compared before they are called equal. */
export type DedupeFold = 'Text' | 'Address' | 'Phone' | 'Email';

export interface DedupeColumn {
  name: string;
  label: string;
  fold: DedupeFold;
}

export interface DuplicateRecord {
  id: number;
  label: string;
  /** The reference columns' values AS STORED - not folded. What decided these
   *  records belong together is the fold; what a person recognises is this. */
  values: Record<string, string | null>;
}

export interface DuplicateGroup {
  key: string;
  recordCount: number;
  records: DuplicateRecord[];
  /** Every ticked column is empty in every record here: they agree by the
   *  rule, and about nothing anybody wrote down. Merge all passes them by. */
  sharesOnlyBlanks: boolean;
}

export interface DuplicateScan {
  referenceColumns: string[];
  groups: DuplicateGroup[];
  totalGroups: number;
  totalRecords: number;
  /** Records the rule judged, which is every record scanned: a blank is a
   *  value, so nothing drops out of the answer for being empty. */
  judged: number;
  /** Groups Merge all would fold - every group but the blank one. */
  mergeableGroups: number;
  /** Records Merge all would fold away, across those groups. */
  mergeableRecords: number;
}

export interface MergeRefusal {
  id: number;
  reason: string;
}

export interface MergeResult {
  merged: number;
  refused: MergeRefusal[];
}

/** Source-owned comparison and projected merge result for one bounded group. */
export interface DuplicateReview {
  records: DuplicateRecord[];
  afterMerge: Record<string, string | null>;
  fieldNotes: Record<string, string>;
  consequence: string;
  token: string;
  editableFields?: string[];
  conflicts?: string[];
}

@Injectable({ providedIn: 'root' })
export class DedupeService {
  constructor(private http: HttpClient) {}

  columns(table: DedupeTable): Observable<DedupeColumn[]> {
    return this.http.get<DedupeColumn[]>(`/api/${table}/dedupe/columns`);
  }

  duplicates(
    table: DedupeTable, columns: string[], page: number, pageSize: number,
  ): Observable<DuplicateScan> {
    return this.http.get<DuplicateScan>(`/api/${table}/dedupe/duplicates`, {
      params: { columns, page, pageSize },
    });
  }

  merge(
    table: DedupeTable, survivorId: number, mergedIds: number[], reviewToken?: string, referenceColumns?: string[], resolutions?: Record<string, string | null>,
  ): Observable<MergeResult> {
    return this.http.post<MergeResult>(
      `/api/${table}/dedupe/merge`, { survivorId, mergedIds, reviewToken, referenceColumns, resolutions });
  }

  review(table: DedupeTable, survivorId: number, mergedIds: number[], referenceColumns: string[]): Observable<DuplicateReview> {
    return this.http.post<DuplicateReview>(`/api/${table}/dedupe/review`, { survivorId, mergedIds, referenceColumns });
  }

}
